import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Globe, Shield, TrendingUp, Link2, Users, BookOpen, Network, BarChart3, Banknote, RefreshCw, Layers } from "lucide-react";

import { NetworkVisual } from "@/components/NetworkVisual";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { PageTransition } from "@/components/PageTransition";
import { FanCarousel } from "@/components/FanCarousel";
import { useT } from "@/lib/i18n";
import { HOMEPAGE } from "@/constants/testIds";

const WHAT_ICONS = [Network, Users, BookOpen];

const PILLAR_META = [
  { key: "access", num: "01", icon: Globe, testId: HOMEPAGE.pillarAccess },
  { key: "trust", num: "02", icon: Shield, testId: HOMEPAGE.pillarTrust },
  { key: "growth", num: "03", icon: TrendingUp, testId: HOMEPAGE.pillarGrowth },
  { key: "connectivity", num: "04", icon: Link2, testId: HOMEPAGE.pillarConnectivity },
];

const SHOWCASE_META = [
  { id: "equities", num: "01", icon: BarChart3, from: "#0D5F5E", to: "#0B4F4E", href: "/markets" },
  { id: "fixed-income", num: "02", icon: Banknote, from: "#0B4F4E", to: "#0D5F5E", href: "/markets" },
  { id: "fx", num: "03", icon: RefreshCw, from: "#0D5F5E", to: "#124E4D", href: "/markets" },
  { id: "derivatives", num: "04", icon: Layers, from: "#0B4F4E", to: "#0D5F5E", href: "/markets" },
  { id: "global", num: "05", icon: Globe, from: "#0D5F5E", to: "#0B4F4E", href: "/markets" },
];

function MarketFace({ item, active }) {
  const Icon = item.icon;
  return (
    <div
      className="relative flex h-full w-full flex-col justify-between p-8"
      style={{ background: `linear-gradient(150deg, ${item.from}, ${item.to})`, color: "#F7F6F2" }}
    >
      {/* coral corner accent that brightens on the active card */}
      <div
        className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full blur-2xl transition-opacity duration-500"
        style={{ background: "rgba(255,104,87,0.5)", opacity: active ? 0.7 : 0.25 }}
        aria-hidden="true"
      />
      <div className="relative flex items-center justify-between">
        <Icon size={24} strokeWidth={1.4} style={{ color: "#BEE5E0" }} />
        <span className="font-display" style={{ fontSize: 22, color: "rgba(190,229,224,0.85)" }}>
          {item.num}<span style={{ color: "#FF6857" }}>.</span>
        </span>
      </div>
      <div className="relative">
        <div className="font-display" style={{ fontSize: 30, lineHeight: 1.05, fontWeight: 500, letterSpacing: "-0.01em" }}>
          {item.title}
        </div>
        <div className="mt-3 h-px" style={{ background: "#FF6857", width: 36 }} />
        <p className="font-ui mt-4" style={{ color: "rgba(247,246,242,0.8)", lineHeight: 1.6, fontSize: 14 }}>
          {item.description}
        </p>
      </div>
    </div>
  );
}

export default function Home() {
  const t = useT();
  const WHAT_WE_DO = t.home.whatWeDo.map((it, i) => ({ ...it, icon: WHAT_ICONS[i] }));
  const PILLARS = PILLAR_META.map((m, i) => ({ ...m, ...t.home.pillars[i] }));
  const MARKETS_SHOWCASE = SHOWCASE_META.map((m, i) => ({ ...m, ...t.home.showcase[i] }));
  return (
    <PageTransition>
      {/* HERO */}
      <section
        data-testid={HOMEPAGE.hero}
        className="relative overflow-hidden"
        style={{ minHeight: "100vh", background: "#0D5F5E", color: "#F7F6F2", isolation: "isolate" }}
      >
        {/* Network visualization on the right */}
        <div className="pointer-events-none absolute inset-y-0 right-0 hidden lg:block lg:w-[55%]">
          <div className="relative h-full w-full">
            <NetworkVisual className="absolute inset-0 h-full w-full" />
          </div>
        </div>
        <div className="pointer-events-none absolute inset-0 lg:hidden opacity-40">
          <NetworkVisual className="h-full w-full" />
        </div>

        {/* Soft vignette */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(70% 60% at 10% 30%, rgba(13,95,94,0.55), transparent 60%), linear-gradient(180deg, rgba(13,95,94,0.0) 60%, rgba(13,95,94,0.45) 100%)",
          }}
          aria-hidden="true"
        />

        {/* fade the teal hero into the cream section below */}
        <div className="hero-fade-bottom" style={{ "--fade-to": "var(--nexxis-bg)" }} aria-hidden="true" />

        <div className="relative z-10 mx-auto flex min-h-[100vh] max-w-[1200px] flex-col justify-center px-5 sm:px-8 pt-32 pb-20">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-[640px]"
          >
            <div className="kicker mb-6" style={{ color: "rgba(247,246,242,0.7)" }}>
              {t.home.heroKicker}
            </div>
            <h1
              className="font-display"
              style={{
                color: "#F7F6F2",
                fontSize: "clamp(40px, 6vw, 76px)",
                lineHeight: 1.05,
                letterSpacing: "-0.02em",
                fontWeight: 500,
              }}
            >
              {t.home.heroTitle}
            </h1>
            <p
              className="font-ui mt-7 max-w-[520px]"
              style={{ color: "rgba(247,246,242,0.82)", fontSize: 17, lineHeight: 1.6 }}
            >
              {t.home.heroBody}
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-5">
              <Link
                to="/markets"
                data-testid={HOMEPAGE.heroExplore}
                className="inline-flex items-center gap-3 rounded-[10px] px-5 py-3 font-ui text-[13px] transition-colors"
                style={{
                  background: "#F7F6F2",
                  color: "#0D5F5E",
                  letterSpacing: "0.04em",
                  fontWeight: 600,
                }}
              >
                <span>{t.home.heroCta1}</span>
                <ArrowRight size={14} />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 rounded-[10px] px-5 py-3 font-ui text-[13px]"
                style={{
                  background: "rgba(247,246,242,0.06)",
                  border: "1px solid rgba(247,246,242,0.32)",
                  color: "#F7F6F2",
                  letterSpacing: "0.04em",
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                }}
              >
                <span>{t.home.heroCta2}</span>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section
        className="relative paper-grain"
        style={{ background: "var(--nexxis-bg)" }}
      >
        <div className="mx-auto max-w-[1200px] px-5 sm:px-8 py-24 lg:py-28">
          <div className="max-w-[640px]">
            <Reveal>
              <div className="kicker mb-5">{t.home.whatKicker}</div>
              <h2
                className="font-display"
                style={{
                  fontSize: "clamp(32px, 4vw, 52px)",
                  lineHeight: 1.1,
                  letterSpacing: "-0.018em",
                  color: "var(--nexxis-ink)",
                  fontWeight: 500,
                }}
              >
                {t.home.whatTitle}
              </h2>
            </Reveal>
          </div>

          <Stagger className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {WHAT_WE_DO.map((it, i) => (
              <StaggerItem key={i}>
                <div
                  className="nxx-card-tilt nxx-card--fill h-full p-7 rounded-[14px]"
                  style={{
                    background: "var(--nexxis-bg-alt)",
                    border: "1px solid var(--nexxis-border)",
                  }}
                >
                  <div
                    className="flex h-10 w-10 items-center justify-center rounded-full"
                    style={{ background: "#0D5F5E", color: "#F7F6F2" }}
                  >
                    <it.icon size={18} strokeWidth={1.6} />
                  </div>
                  <div
                    className="font-display mt-6"
                    style={{
                      fontSize: 26,
                      lineHeight: 1.1,
                      letterSpacing: "-0.01em",
                      color: "var(--nexxis-ink)",
                      fontWeight: 500,
                    }}
                  >
                    {it.title}
                  </div>
                  <div className="mt-2 h-px" style={{ background: "#FF6857", width: 32 }} />
                  <p
                    className="font-ui mt-4"
                    style={{ color: "var(--nexxis-ink-muted)", lineHeight: 1.65, fontSize: 14 }}
                  >
                    {it.body}
                  </p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* PILLARS */}
      <section
        data-testid={HOMEPAGE.pillars}
        className="relative"
        style={{ background: "var(--nexxis-bg-alt)" }}
      >
        <div className="mx-auto max-w-[1200px] px-5 sm:px-8 py-24 lg:py-28">
          <div className="grid grid-cols-12 items-end gap-6 mb-12">
            <Reveal as="div" className="col-span-12 lg:col-span-8">
              <div className="kicker mb-5">{t.home.pillarsKicker}</div>
              <h2
                className="font-display"
                style={{
                  fontSize: "clamp(32px, 4vw, 52px)",
                  lineHeight: 1.1,
                  letterSpacing: "-0.018em",
                  color: "var(--nexxis-ink)",
                  fontWeight: 500,
                }}
              >
                {t.home.pillarsTitle}
              </h2>
            </Reveal>
            <Reveal as="div" delay={0.06} className="col-span-12 lg:col-span-4">
              <p
                className="font-ui"
                style={{
                  color: "var(--nexxis-ink-muted)",
                  lineHeight: 1.7,
                  fontSize: 14,
                  maxWidth: 360,
                }}
              >
                {t.home.pillarsLede}
              </p>
            </Reveal>
          </div>

          <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {PILLARS.map((p) => (
              <StaggerItem key={p.key}>
                <div
                  data-testid={p.testId}
                  className="nxx-card nxx-card--glow relative h-full p-7 rounded-[14px] overflow-hidden"
                  style={{
                    background: "#0D5F5E",
                    color: "#F7F6F2",
                    minHeight: 280,
                  }}
                >
                  <div
                    className="flex items-center justify-between"
                  >
                    <p.icon size={20} strokeWidth={1.4} style={{ color: "#BEE5E0" }} />
                    <span
                      className="font-display"
                      style={{ fontSize: 22, color: "rgba(190,229,224,0.85)", letterSpacing: "-0.005em" }}
                    >
                      {p.num}<span style={{ color: "#FF6857" }}>.</span>
                    </span>
                  </div>
                  <div
                    className="font-display mt-10"
                    style={{
                      fontSize: 30,
                      lineHeight: 1.05,
                      letterSpacing: "-0.01em",
                      color: "#F7F6F2",
                      fontWeight: 500,
                    }}
                  >
                    {p.title}
                  </div>
                  <div className="mt-3 h-px" style={{ background: "#FF6857", width: 32 }} />
                  <p
                    className="font-ui mt-4"
                    style={{ color: "rgba(247,246,242,0.78)", lineHeight: 1.65, fontSize: 14 }}
                  >
                    {p.body}
                  </p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* MARKETS SHOWCASE — 3D fan carousel */}
      <section className="relative overflow-hidden" style={{ background: "var(--nexxis-bg)" }}>
        <div className="mx-auto max-w-[1200px] px-5 sm:px-8 py-24 lg:py-28">
          <div className="grid grid-cols-12 items-end gap-6 mb-10">
            <Reveal as="div" className="col-span-12 lg:col-span-8">
              <div className="kicker mb-5">{t.home.marketsKicker}</div>
              <h2
                className="font-display"
                style={{
                  fontSize: "clamp(32px, 4vw, 52px)",
                  lineHeight: 1.1,
                  letterSpacing: "-0.018em",
                  color: "var(--nexxis-ink)",
                  fontWeight: 500,
                }}
              >
                {t.home.marketsTitle}
              </h2>
            </Reveal>
            <Reveal as="div" delay={0.06} className="col-span-12 lg:col-span-4">
              <p
                className="font-ui"
                style={{ color: "var(--nexxis-ink-muted)", lineHeight: 1.7, fontSize: 14, maxWidth: 360 }}
              >
                {t.home.marketsLede}
              </p>
            </Reveal>
          </div>

          <Reveal fade>
            <FanCarousel
              items={MARKETS_SHOWCASE}
              autoAdvance
              intervalMs={3600}
              renderCard={(item, state) => <MarketFace item={item} active={state.active} />}
            />
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section
        data-testid={HOMEPAGE.editorialIntro}
        className="relative paper-grain"
        style={{ background: "var(--nexxis-bg)" }}
      >
        <div className="mx-auto max-w-[1200px] px-5 sm:px-8 py-24">
          <Reveal>
            <div className="grid grid-cols-12 gap-6 items-end">
              <div className="col-span-12 lg:col-span-8">
                <h3
                  className="font-display"
                  style={{
                    fontSize: "clamp(28px, 3.4vw, 44px)",
                    lineHeight: 1.1,
                    letterSpacing: "-0.012em",
                    color: "var(--nexxis-ink)",
                    fontWeight: 500,
                  }}
                >
                  {t.home.ctaTitle}
                </h3>
                <p
                  className="font-ui mt-5"
                  style={{ color: "var(--nexxis-ink-muted)", lineHeight: 1.7, fontSize: 16, maxWidth: 540 }}
                >
                  {t.home.ctaBody}
                </p>
              </div>
              <div className="col-span-12 lg:col-span-4 flex lg:justify-end">
                <Link
                  to="/contact"
                  data-testid={HOMEPAGE.discoverLink}
                  className="inline-flex items-center gap-3 rounded-[10px] px-5 py-3 font-ui text-[13px]"
                  style={{
                    background: "#0D5F5E",
                    color: "#F7F6F2",
                    letterSpacing: "0.04em",
                    fontWeight: 600,
                  }}
                >
                  <span>{t.home.ctaLink}</span>
                  <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </PageTransition>
  );
}
